import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2, Loader2, Phone, MessageCircle, Mail, MapPin, Users, RefreshCw, Gift, Pencil, Copy, Check, Search, CalendarDays } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { normalize as _nz } from "../lib/search";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MSHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const TYPE_META = {
  Llamada: { icon: Phone, color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  WhatsApp: { icon: MessageCircle, color: "bg-green-100 text-green-700", dot: "bg-green-500" },
  Correo: { icon: Mail, color: "bg-sky-100 text-sky-700", dot: "bg-sky-500" },
  Visita: { icon: MapPin, color: "bg-primary/15 text-primary", dot: "bg-primary" },
  Reunión: { icon: Users, color: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
  Seguimiento: { icon: RefreshCw, color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  Recordatorio: { icon: Gift, color: "bg-pink-100 text-pink-700", dot: "bg-pink-500" },
};
const TYPES = Object.keys(TYPE_META);
const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => { const t = new Date(); return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`; };
const fmtDate = (d) => { const [y, m, dd] = d.split("-"); return `${dd} ${MSHORT[Number(m) - 1]} ${y}`; };
const emptyForm = () => ({ title: "", type: "Llamada", date: todayStr(), start_time: "", client: "", contact: "", priority: "Normal", description: "", status: "Pendiente", link_type: "", link_id: "" });

function Kpi({ label, value, tone, testid }) {
  return (
    <Card className={`rounded-sm border shadow-none p-4 ${tone}`} data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-widest opacity-70">{label}</p>
      <p className="text-3xl font-heading font-bold mt-1">{value}</p>
    </Card>
  );
}

export default function Calendar() {
  const { can } = useAuth();
  const today = new Date();
  const [cur, setCur] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [data, setData] = useState({ activities: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(todayStr());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [cold, setCold] = useState([]);
  const [linkType, setLinkType] = useState("free");
  const [linkSearch, setLinkSearch] = useState("");
  const [linkContacts, setLinkContacts] = useState([]);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fType, setFType] = useState("all");
  const monthStr = `${cur.y}-${pad(cur.m + 1)}`;

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/agenda?month=${monthStr}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [monthStr]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/clients"), api.get("/prospects?segment=prospect"), api.get("/prospects?segment=cold")])
      .then(([c, p, cd]) => { setClients(c.data || []); setProspects(p.data || []); setCold(cd.data || []); })
      .catch(() => {});
  }, []);

  const activities = data.activities || [];
  const s = data.summary || {};
  const isOverdue = (a) => a.status === "Pendiente" && a.type !== "Recordatorio" && a.date < todayStr();

  const nav = (d) => { let m = cur.m + d, y = cur.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } setCur({ y, m }); };
  const firstDow = (new Date(cur.y, cur.m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(cur.y, cur.m + 1, 0).getDate();
  const cells = []; for (let i = 0; i < firstDow; i++) cells.push(null); for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const actOf = (ds) => activities.filter((a) => a.date === ds);

  const weekStrip = useMemo(() => {
    const base = new Date(); const arr = [];
    for (let i = 0; i < 7; i++) { const d = new Date(base); d.setDate(base.getDate() + i); arr.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`); }
    return arr;
  }, []);

  const byType = useMemo(() => {
    const m = {}; TYPES.forEach((t) => (m[t] = 0));
    activities.forEach((a) => { if (m[a.type] !== undefined) m[a.type]++; });
    return m;
  }, [activities]);
  const maxType = Math.max(1, ...Object.values(byType));

  const upcoming = useMemo(() => activities
    .filter((a) => a.status !== "Realizada" && a.date >= todayStr() && a.type !== "Recordatorio")
    .sort((a, b) => a.date.localeCompare(b.date)).slice(0, 5), [activities]);

  const listFiltered = activities.filter((a) => {
    const text = _nz(`${a.title} ${a.client} ${a.contact} ${a.description}`);
    if (q && !text.includes(_nz(q))) return false;
    if (fType !== "all" && a.type !== fType) return false;
    if (fStatus === "Realizada" && a.status !== "Realizada") return false;
    if (fStatus === "Pendiente" && a.status !== "Pendiente") return false;
    if (fStatus === "Vencida" && !isOverdue(a)) return false;
    return true;
  });

  const linkOptions = useMemo(() => {
    const src = linkType === "client" ? clients : linkType === "prospect" ? prospects : linkType === "cold" ? cold : [];
    return src.map((o) => ({ id: o.id, name: o.business_name || o.company || o.name || o.contact_name || "(sin nombre)", comm: o.commercial_name || "", contact: o.contact_name || o.contact || "" }));
  }, [linkType, clients, prospects, cold]);
  const linkFiltered = useMemo(() => {
    const nq = _nz(linkSearch);
    return linkOptions.filter((o) => !nq || _nz(o.name).includes(nq) || _nz(o.comm).includes(nq)).slice(0, 30);
  }, [linkOptions, linkSearch]);
  const pickLink = async (o) => { setForm((f) => ({ ...f, client: o.name, link_id: o.id, link_type: linkType, contact: o.contact || f.contact })); setLinkSearch(o.name); setLinkContacts([]); if (linkType === "client") { try { const r = await api.get("/contacts", { params: { client_id: o.id } }); setLinkContacts(r.data || []); } catch { setLinkContacts([]); } } };
  const changeLinkType = (v) => { setLinkType(v); setLinkSearch(""); setLinkContacts([]); setForm((f) => ({ ...f, link_type: v === "free" ? "" : v, link_id: "", client: v === "free" ? f.client : "" })); };

  const openNew = (type, date) => { setEditing(null); setForm({ ...emptyForm(), type: type || "Llamada", date: date || selectedDay || todayStr() }); setLinkType("free"); setLinkSearch(""); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ title: a.title, type: a.type, date: a.date, start_time: a.start_time || "", client: a.client || "", contact: a.contact || "", priority: a.priority || "Normal", description: a.description || "", status: a.status || "Pendiente", link_type: a.link_type || "", link_id: a.link_id || "" }); setLinkType(a.link_type || "free"); setLinkSearch(a.client || ""); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/calendar/${editing.id}`, form);
      else await api.post("/calendar", form);
      toast.success("Actividad guardada"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markDone = async (a) => { try { await api.post(`/calendar/${a.id}/status`, { status: a.status === "Realizada" ? "Pendiente" : "Realizada" }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const duplicate = async (a) => { try { await api.post(`/calendar/${a.id}/duplicate`); toast.success("Actividad duplicada"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (a) => { try { await api.delete(`/calendar/${a.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const ActivityRow = ({ a, compact }) => {
    const meta = TYPE_META[a.type] || TYPE_META.Llamada; const Icon = meta.icon; const overdue = isOverdue(a);
    return (
      <div className={`border rounded-sm p-3 ${overdue ? "border-red-500/20 bg-red-500/10" : "border-border"}`} data-testid={`activity-${a.id}`}>
        <div className="flex items-start gap-3">
          <div className={`h-8 w-8 rounded-sm flex items-center justify-center shrink-0 ${meta.color}`}><Icon className="h-4 w-4" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-sm">{a.title}</p>
              <Badge className={`rounded-sm text-[10px] ${meta.color}`}>{a.type}</Badge>
              {a.status === "Realizada" && <Badge className="rounded-sm text-[10px] bg-green-100 text-green-700">Realizada</Badge>}
              {overdue && <Badge className="rounded-sm text-[10px] bg-red-100 text-red-700">Vencida</Badge>}
              {a.priority === "Alta" && <Badge className="rounded-sm text-[10px] bg-orange-100 text-orange-700">Alta</Badge>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{fmtDate(a.date)}{a.start_time ? ` · ${a.start_time}` : ""}{a.client ? ` · ${a.client}` : ""}{a.contact ? ` · ${a.contact}` : ""}</p>
            {!compact && a.description && <p className="text-xs mt-1">{a.description}</p>}
          </div>
        </div>
        {a.editable !== false && (
          <div className="flex gap-1.5 mt-2 flex-wrap">
            <Button size="sm" variant={a.status === "Realizada" ? "outline" : "default"} className="rounded-sm h-7 text-xs gap-1" onClick={() => markDone(a)} data-testid={`done-${a.id}`}><Check className="h-3 w-3" /> {a.status === "Realizada" ? "Reabrir" : "Realizada"}</Button>
            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs gap-1" onClick={() => openEdit(a)} data-testid={`edit-${a.id}`}><Pencil className="h-3 w-3" /> Editar</Button>
            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs gap-1" onClick={() => duplicate(a)}><Copy className="h-3 w-3" /> Duplicar</Button>
            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs text-destructive gap-1" onClick={() => del(a)} data-testid={`del-${a.id}`}><Trash2 className="h-3 w-3" /></Button>
          </div>
        )}
        {a.source === "visit" && <p className="text-[10px] text-muted-foreground mt-1.5 italic">Gestionada desde el módulo Visitas</p>}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Agenda comercial</h1>
          <p className="text-sm text-muted-foreground max-w-xl">Organiza llamadas, WhatsApp, correos, visitas, reuniones y seguimientos. Prioriza lo vencido y lo que mueve ventas hoy.</p>
        </div>
        {can("calendar:create") && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => openNew()} className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs" data-testid="new-activity-btn"><Plus className="h-4 w-4" /> Nueva actividad</Button>
            <Button variant="outline" className="rounded-sm gap-1.5 text-xs" onClick={() => openNew("Visita")} data-testid="quick-visita"><MapPin className="h-3.5 w-3.5 text-primary" /> Visita</Button>
            <Button variant="outline" className="rounded-sm gap-1.5 text-xs" onClick={() => openNew("Llamada")} data-testid="quick-llamada"><Phone className="h-3.5 w-3.5 text-blue-600" /> Llamada</Button>
            <Button variant="outline" className="rounded-sm gap-1.5 text-xs" onClick={() => openNew("WhatsApp")} data-testid="quick-whatsapp"><MessageCircle className="h-3.5 w-3.5 text-green-600" /> WhatsApp</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Hoy" value={s.today_pending ?? 0} tone="" testid="kpi-today" />
        <Kpi label="Vencidas" value={s.overdue ?? 0} tone={s.overdue ? "border-red-500/20 bg-red-500/10" : ""} testid="kpi-overdue" />
        <Kpi label="Próx. 7 días" value={s.next7_pending ?? 0} tone="" testid="kpi-week" />
        <Kpi label="Visitas pend." value={s.visits_pending ?? 0} tone="border-primary/20 bg-primary/5" testid="kpi-visits" />
        <Kpi label="Realizadas" value={s.done_count ?? 0} tone="border-green-500/20 bg-green-500/10" testid="kpi-done" />
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-4">
            <Card className="rounded-sm border shadow-none overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h2 className="font-heading font-bold">{MONTHS[cur.m]} {cur.y}</h2>
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" onClick={() => nav(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="rounded-sm text-xs" onClick={() => { setCur({ y: today.getFullYear(), m: today.getMonth() }); setSelectedDay(todayStr()); }}>Hoy</Button>
                  <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" onClick={() => nav(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-7 bg-muted text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {DOW.map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
              </div>
              <div className="grid grid-cols-7" data-testid="agenda-calendar">
                {cells.map((d, i) => {
                  if (!d) return <div key={i} className="min-h-[76px] border-t border-r border-border bg-muted/20" />;
                  const ds = `${monthStr}-${pad(d)}`; const items = actOf(ds); const isT = ds === todayStr(); const sel = ds === selectedDay;
                  return (
                    <div key={i} className={`min-h-[76px] border-t border-r border-border p-1.5 cursor-pointer hover:bg-muted/40 ${sel ? "ring-2 ring-primary ring-inset" : ""}`} onClick={() => setSelectedDay(ds)} data-testid={`day-${d}`}>
                      <span className={`text-xs font-semibold inline-flex items-center justify-center h-6 w-6 rounded-sm ${isT ? "bg-primary text-primary-foreground" : ""}`}>{d}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {items.slice(0, 4).map((a) => <span key={a.id} className={`h-1.5 w-1.5 rounded-full ${TYPE_META[a.type]?.dot || "bg-muted-foreground"}`} />)}
                      </div>
                      {items.length > 0 && <p className="text-[10px] text-muted-foreground mt-0.5">{items.length} act.</p>}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Listado */}
            <Card className="rounded-sm border shadow-none p-4">
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Buscar actividad, cliente, contacto, nota" className="rounded-sm pl-8" value={q} onChange={(e) => setQ(e.target.value)} data-testid="agenda-search" />
                </div>
                <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="rounded-sm w-full sm:w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todos los estados</SelectItem><SelectItem value="Pendiente">Pendiente</SelectItem><SelectItem value="Vencida">Vencida</SelectItem><SelectItem value="Realizada">Realizada</SelectItem></SelectContent></Select>
                <Select value={fType} onValueChange={setFType}><SelectTrigger className="rounded-sm w-full sm:w-40" data-testid="filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todos los tipos</SelectItem>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <p className="text-xs text-muted-foreground mb-2">{listFiltered.length} actividad(es) encontrada(s).</p>
              <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1" data-testid="agenda-list">
                {listFiltered.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Sin actividades con estos filtros.</p> :
                  listFiltered.sort((a, b) => b.date.localeCompare(a.date)).map((a) => <ActivityRow key={a.id} a={a} />)}
              </div>
            </Card>
          </div>

          <div className="space-y-4">
            <Card className="rounded-sm border shadow-none p-4" data-testid="agenda-myday">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /><h3 className="font-heading font-bold">Mi día</h3></div>
                {can("calendar:create") && <Button size="sm" className="rounded-sm h-7 text-xs gap-1" onClick={() => openNew(null, selectedDay)} data-testid="add-activity-day"><Plus className="h-3 w-3" /> Actividad</Button>}
              </div>
              <p className="text-xs text-muted-foreground mb-3">{fmtDate(selectedDay)}</p>
              <div className="space-y-2">
                {actOf(selectedDay).length === 0 ? <p className="text-sm text-muted-foreground">No hay actividades para este día.</p> :
                  actOf(selectedDay).map((a) => <ActivityRow key={a.id} a={a} compact />)}
              </div>
            </Card>

            <Card className="rounded-sm border shadow-none p-4">
              <h3 className="font-heading font-bold mb-1">Próximas</h3>
              <p className="text-xs text-muted-foreground mb-3">Actividades pendientes más cercanas.</p>
              <div className="space-y-2">
                {upcoming.length === 0 ? <p className="text-sm text-muted-foreground">Sin próximas actividades.</p> :
                  upcoming.map((a) => <ActivityRow key={a.id} a={a} compact />)}
              </div>
            </Card>

            <Card className="rounded-sm border shadow-none p-4">
              <h3 className="font-heading font-bold mb-1">Actividad por tipo</h3>
              <p className="text-xs text-muted-foreground mb-3">Distribución del mes.</p>
              <div className="space-y-2">
                {TYPES.map((t) => (
                  <div key={t} className="flex items-center gap-2" data-testid={`dist-${t}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${TYPE_META[t].dot}`} />
                    <span className="text-xs w-24">{t}</span>
                    <div className="flex-1 bg-muted rounded-sm h-2 overflow-hidden"><div className={`h-full ${TYPE_META[t].dot}`} style={{ width: `${(byType[t] / maxType) * 100}%` }} /></div>
                    <span className="text-xs font-mono w-6 text-right">{byType[t]}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="activity-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar actividad" : "Nueva actividad"}</DialogTitle>
            <DialogDescription>Programa una acción comercial en tu agenda.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Título / asunto" className="rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="activity-title" />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="rounded-sm" data-testid="activity-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.filter((t) => t !== "Recordatorio").map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Fecha</Label>
                <Input type="date" className="rounded-sm" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="activity-date" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Hora</Label>
                <Input type="time" className="rounded-sm" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Prioridad</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Normal">Normal</SelectItem><SelectItem value="Alta">Alta</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Relacionar con</Label>
              <Select value={linkType} onValueChange={changeLinkType}>
                <SelectTrigger className="rounded-sm" data-testid="activity-link-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Libre (escribir)</SelectItem>
                  <SelectItem value="client">Cliente</SelectItem>
                  <SelectItem value="cold">Cliente frío</SelectItem>
                  <SelectItem value="prospect">Prospecto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {linkType === "free" ? (
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Cliente / empresa" className="rounded-sm" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} data-testid="activity-client" />
                <Input placeholder="Contacto" className="rounded-sm" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="activity-contact" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input placeholder={`Buscar ${linkType === "client" ? "cliente" : linkType === "cold" ? "cliente frío" : "prospecto"}`} className="rounded-sm pl-8" value={linkSearch} onChange={(e) => { setLinkSearch(e.target.value); setForm({ ...form, link_id: "", client: e.target.value }); }} data-testid="activity-link-search" />
                  </div>
                  {!form.link_id && (
                    <div className="border border-border rounded-sm max-h-44 overflow-y-auto" data-testid="activity-link-results">
                      {linkFiltered.length === 0 ? <p className="text-xs text-muted-foreground p-2">Sin registros. Escribe para buscar o agrégalo manual en "Libre".</p> :
                        linkFiltered.map((o) => (
                          <button key={o.id} type="button" className="block w-full text-left px-2.5 py-1.5 text-sm hover:bg-muted" onClick={() => pickLink(o)} data-testid={`link-opt-${o.id}`}>{o.name}</button>
                        ))}
                    </div>
                  )}
                  {form.link_id && <button type="button" onClick={() => { setForm({ ...form, link_id: "", client: "" }); setLinkSearch(""); setLinkContacts([]); }} className="text-[11px] text-primary font-semibold flex items-center gap-1 mt-1" data-testid="activity-link-clear"><Check className="h-3 w-3" /> {form.client} · cambiar</button>}
                </div>
                <div className="space-y-1.5">
                  {linkContacts.length > 0 && (
                    <Select onValueChange={(v) => { const c = linkContacts.find((x) => x.id === v); if (c) setForm((f) => ({ ...f, contact: `${c.name || ""}${c.last_name ? " " + c.last_name : ""}`.trim() + ((c.phone || c.mobile) ? ` · ${c.phone || c.mobile}` : "") })); }}>
                      <SelectTrigger className="rounded-sm" data-testid="activity-contact-picker"><SelectValue placeholder="Elegir contacto guardado" /></SelectTrigger>
                      <SelectContent>{linkContacts.map((c) => <SelectItem key={c.id} value={c.id}>{`${c.name || ""}${c.last_name ? " " + c.last_name : ""}`.trim() || "(sin nombre)"}{c.position ? ` — ${c.position}` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <Input placeholder="Contacto (o escribe manual)" className="rounded-sm" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="activity-contact" />
                </div>
              </div>
            )}
            <Textarea placeholder="Nota / detalle" className="rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            {editing && (
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Pendiente">Pendiente</SelectItem><SelectItem value="Realizada">Realizada</SelectItem></SelectContent></Select></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={save} disabled={!form.title || !form.date} data-testid="save-activity-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
